import {
  App,
  Button,
  Form,
  FormInstance,
  type FormProps,
  Popconfirm,
} from "antd"
import { noop } from "lodash"
import {
  cloneElement,
  type ReactElement,
  useCallback,
  useMemo,
  useState,
} from "react"

import CopyToClipboard from "#blocks/CopyToClipboard"
import Flex from "#blocks/Flex"
import Modal from "#blocks/Modal"
import FormWithValues from "#components/FormWithValues"
import { useUserContext } from "#contexts/UserContextProvider"
import {
  Resource,
  RESOURCES,
  USER_ACTIONS,
} from "#customTypes/composedTypes/authTypes"
import { ReactChild } from "#customTypes/globalTypes"
import { type FormData } from "#customTypes/types"
import useHasPermission, {
  useCanSeeIds,
} from "#hooks/newHooks/useHasPermission"
import { useErrorModal } from "#hooks/useErrorModal"
import { cleanFormData } from "#utils/formHelpers"
import { camelToWords, ErrorType } from "#utils/textFormatters"

export interface GenericFormModalProps<FormValues, SubmitValues> {
  title: string | ReactChild
  onClose?: () => void
  initialValues: FormValues | undefined | null
  children: ReactElement<{
    onClose: () => void
    initialValues?: FormValues
  }>
  onFinish?: (values: SubmitValues, form?: FormInstance) => Promise<void>
  onFinishFailed?: FormProps<FormValues>["onFinishFailed"]
  resource: Resource
  onDelete?: () => Promise<void>
  width?: number
}

const GenericFormModal = <
  FormValues extends FormData,
  SubmitValues extends FormData,
>({
  title,
  onClose,
  initialValues,
  children,
  onFinish,
  onFinishFailed,
  resource,
  onDelete,
  width,
}: GenericFormModalProps<FormValues, SubmitValues>) => {
  const [form] = Form.useForm<FormValues>()
  const [isOpen, setIsOpen] = useState(true)
  const { currentUserId } = useUserContext()
  const canDelete = useHasPermission(RESOURCES[resource], USER_ACTIONS.DELETE)
  const canSeeIds = useCanSeeIds()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const openErrorModal = useErrorModal(`using ${camelToWords(resource)} form`)

  const handleClose = useCallback(() => {
    setIsOpen(false)
    // Leave time for the animation to finish
    setTimeout(() => {
      onClose?.()
    }, 500)
  }, [onClose])

  const { message, modal } = App.useApp()

  const handleCloseAttempt = useCallback(() => {
    if (form.isFieldsTouched()) {
      modal.confirm({
        title: "Unsaved Changes",
        content:
          "You have unsaved changes. Are you sure you want to close without saving?",
        okText: "Yes, close",
        cancelText: "No",
        onOk: handleClose,
        onCancel: noop,
        autoFocusButton: "ok",
      })
      return
    }

    handleClose()
  }, [form, handleClose, modal])

  const handleFinish = async (values: FormValues): Promise<void> => {
    try {
      setIsSubmitting(true)
      const cleanedValues = cleanFormData(values) as SubmitValues
      // Don't use mocks here
      const now = new Date().toISOString()

      const submitData: SubmitValues = {
        ...cleanedValues,
        createdAt: values.createdAt ?? now,
        createdBy: values.createdBy ?? currentUserId,
        lastUpdatedAt: now,
        lastUpdatedBy: currentUserId,
      }

      await onFinish?.(submitData, form)
      void message.success(`${camelToWords(resource)} saved successfully.`)
      handleClose()
    } catch (error) {
      const context = `
${camelToWords(resource)} form submit

${resource} ID: ${values?.id}
`
      openErrorModal(error as ErrorType, context)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = useCallback(async () => {
    try {
      setIsSubmitting(true)
      await onDelete?.()
      void message.success(`${camelToWords(resource)} deleted successfully.`)
      handleClose()
    } catch (error) {
      const context = `
${camelToWords(resource)} form delete

${resource} ID: ${initialValues?.id}
`
      openErrorModal(error as ErrorType, context)
    } finally {
      setIsSubmitting(false)
    }
  }, [
    onDelete,
    handleClose,
    message,
    resource,
    openErrorModal,
    initialValues?.id,
  ])

  const footer = useMemo(() => {
    const result = [
      <Button key="cancel" onClick={handleCloseAttempt}>
        Cancel
      </Button>,
      <Button
        key="submit"
        onClick={form.submit}
        type="primary"
        loading={isSubmitting}
      >
        Submit
      </Button>,
    ]

    if (initialValues?.id && canDelete) {
      result.unshift(
        <Popconfirm
          key="delete"
          title={"Are you sure?"}
          onConfirm={handleDelete}
          okText="Yes, delete"
          cancelText="No"
          okButtonProps={{
            loading: isSubmitting,
          }}
        >
          <Button danger loading={isSubmitting}>
            Delete
          </Button>
        </Popconfirm>
      )
    }
    return result
  }, [
    form.submit,
    isSubmitting,
    handleCloseAttempt,
    handleDelete,
    initialValues?.id,
    canDelete,
  ])

  return (
    <Modal
      width={width || 1200}
      open={isOpen}
      footer={footer}
      onClose={handleCloseAttempt}
      title={
        <Flex
          gap={8}
          style={{
            alignItems: "center",
            justifyContent: "space-between",
            marginRight: 30,
            fontSize: 18,
          }}
        >
          {title}
          {initialValues?.id && canSeeIds ? (
            <CopyToClipboard
              data={initialValues.id}
              label={`ID: ${initialValues.id}`}
            />
          ) : null}
        </Flex>
      }
    >
      <FormWithValues
        form={form}
        onFinish={handleFinish}
        onFinishFailed={onFinishFailed}
        name="makeup"
        layout="vertical"
        initialValues={initialValues || undefined}
        clearOnDestroy
      >
        {children
          ? cloneElement(children, {
              onClose,
              initialValues: initialValues || undefined,
            })
          : null}

        {/* {error && <FormError error={error} message="Something went wrong." />} */}
      </FormWithValues>
    </Modal>
  )
}

export default GenericFormModal
